import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  VertexCreditMemoErrorOutput,
  VertexCreditMemoPassOutput,
} from "models/vertex-credit-memo";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StatusChip from "../general/StatusChip";
import { useCreditMemoSendRequest } from "src/hooks/credit-memo-send/useCreditMemoSendRequest";
import { getNetSuiteLink } from "src/util/helpers";

const CreditMemoSend: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const { request, loading, refetch } = useCreditMemoSendRequest(id!);

  const fetchRecord = useCallback(async () => {
    if (id) {
      setRefreshing(true);
      try {
        await refetch();
      } finally {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchRecord();
  }, []);

  useEffect(() => {
    if (request?.status === "IN_PROGRESS") {
      const intervalId = setInterval(() => {
        fetchRecord();
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [request?.status, fetchRecord]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="body1">
          No credit memo send record found.
        </Typography>
      </Box>
    );
  }

  const renderOutputDetails = () => {
    const output = request.output;
    if (output.decision === "PASS") {
      const pass = output as VertexCreditMemoPassOutput;
      return (
        <>
          {pass.creditMemoId && (
            <Typography variant="body2">
              <strong>Credit Memo:</strong>{" "}
              <a
                href={getNetSuiteLink("CreditMemo", pass.creditMemoId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {pass.creditMemoTranId}
              </a>
            </Typography>
          )}
          {pass.amount != null && (
            <Typography variant="body2">
              <strong>Amount:</strong> {pass.amount}
            </Typography>
          )}
          {pass.memo && (
            <Typography variant="body2">
              <strong>Memo:</strong> {pass.memo}
            </Typography>
          )}
          {pass.subsidiaryName && (
            <Typography variant="body2">
              <strong>Subsidiary:</strong> {pass.subsidiaryName} (
              {pass.subsidiaryId})
            </Typography>
          )}
          {pass.customerCountry && (
            <Typography variant="body2">
              <strong>Customer Country:</strong> {pass.customerCountry}
            </Typography>
          )}
          {pass.appliedInvoices && pass.appliedInvoices.length > 0 && (
            <Typography variant="body2">
              <strong>Applied Invoices:</strong>{" "}
              {pass.appliedInvoices.map((inv) => inv.tranId).join(", ")}
            </Typography>
          )}
          <Typography variant="h6" sx={{ mt: 2 }}>
            PDF email (NetSuite)
          </Typography>
          <Typography variant="body2">
            <strong>PDF Already Sent:</strong>{" "}
            {pass.pdfAlreadySent ? "Yes" : "No"}
          </Typography>
          {pass.pdfMessageId && (
            <Typography variant="body2">
              <strong>PDF Message ID:</strong> {pass.pdfMessageId}
            </Typography>
          )}
          {pass.pdfMessageUrl && (
            <Typography variant="body2">
              <strong>PDF Message:</strong>{" "}
              <a
                href={pass.pdfMessageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in NetSuite
              </a>
            </Typography>
          )}
          <Typography variant="h6" sx={{ mt: 2 }}>
            XML (Vertex)
          </Typography>
          <Typography variant="body2">
            <strong>Already Sent:</strong> {pass.alreadySent ? "Yes" : "No"}
          </Typography>
          <Typography variant="body2">
            <strong>Vertex Sent:</strong> {pass.vertexSent ? "Yes" : "No"}
          </Typography>
          {pass.vertexMessageId && (
            <Typography variant="body2">
              <strong>Vertex Message ID:</strong> {pass.vertexMessageId}
            </Typography>
          )}
          {pass.vertexStatus && (
            <Typography variant="body2">
              <strong>Vertex Status:</strong> {pass.vertexStatus}
            </Typography>
          )}
        </>
      );
    }

    if (output.decision === "ERROR") {
      const err = output as VertexCreditMemoErrorOutput;
      return (
        <>
          {err.reasoning && (
            <Typography variant="body2">
              <strong>Reasoning:</strong> {err.reasoning}
            </Typography>
          )}
          {err.workUnit &&
            err.workUnit !== "JIRA_CREATE_FAILED" &&
            err.workUnit !== "JIRA_UPDATE_FAILED" && (
            <Typography variant="body2">
              <strong>Work Unit:</strong>{" "}
              <a
                href={`https://devgraph-alp.atlassian.net/browse/${err.workUnit}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {err.workUnit}
              </a>
            </Typography>
          )}
          {err.workUnit === "JIRA_CREATE_FAILED" && (
            <Typography variant="body2">
              <strong>Work Unit:</strong> Jira create failed
            </Typography>
          )}
          {err.workUnit === "JIRA_UPDATE_FAILED" && (
            <Typography variant="body2">
              <strong>Work Unit:</strong> Jira update failed
            </Typography>
          )}
          {err.creditMemoId && (
            <Typography variant="body2">
              <strong>Credit Memo:</strong>{" "}
              <a
                href={getNetSuiteLink("CreditMemo", err.creditMemoId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {err.creditMemoTranId || err.creditMemoId}
              </a>
            </Typography>
          )}
          {err.amount != null && (
            <Typography variant="body2">
              <strong>Amount:</strong> {err.amount}
            </Typography>
          )}
          {err.memo && (
            <Typography variant="body2">
              <strong>Memo:</strong> {err.memo}
            </Typography>
          )}
          <Typography variant="h6" sx={{ mt: 2 }}>
            PDF email (NetSuite)
          </Typography>
          <Typography variant="body2">
            <strong>PDF Already Sent:</strong>{" "}
            {err.pdfAlreadySent ? "Yes" : "No"}
          </Typography>
          {err.pdfMessageUrl && (
            <Typography variant="body2">
              <strong>PDF Message:</strong>{" "}
              <a
                href={err.pdfMessageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in NetSuite
              </a>
            </Typography>
          )}
          <Typography variant="h6" sx={{ mt: 2 }}>
            XML (Vertex)
          </Typography>
          <Typography variant="body2">
            <strong>Vertex Sent:</strong> {err.vertexSent ? "Yes" : "No"}
          </Typography>
          {err.vertexMessageId && (
            <Typography variant="body2">
              <strong>Vertex Message ID:</strong> {err.vertexMessageId}
            </Typography>
          )}
          {err.vertexError && (
            <Typography variant="body2">
              <strong>Vertex Error:</strong> {err.vertexError}
            </Typography>
          )}
        </>
      );
    }

    if ("reasoning" in output && output.reasoning) {
      return (
        <Typography variant="body2">
          <strong>Reasoning:</strong> {output.reasoning}
        </Typography>
      );
    }
    return null;
  };

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3} gap={2}>
          <IconButton onClick={() => navigate("/credit-memos")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">CMSEND-{request.requestId}</Typography>
          <StatusChip status={request.status} />
          <Chip
            label={request.output.decision}
            color={
              request.output.decision === "PASS"
                ? "success"
                : request.output.decision === "ERROR"
                  ? "error"
                  : "default"
            }
          />
          {refreshing && <CircularProgress size={24} />}
        </Box>
        <Box mb={3}>
          <Typography variant="h6">Input Details</Typography>
          <Typography variant="body2">
            <strong>Credit Memo TranId:</strong>{" "}
            {request.input.creditMemoTranId}
          </Typography>
          {request.input.jiraTicketId && (
            <Typography variant="body2">
              <strong>Existing Jira:</strong>{" "}
              <a
                href={`https://devgraph-alp.atlassian.net/browse/${request.input.jiraTicketId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {request.input.jiraTicketId}
              </a>
            </Typography>
          )}
        </Box>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Output Details
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              <strong>Decision:</strong>
            </Typography>
            <Chip
              label={request.output.decision}
              color={
                request.output.decision === "PASS"
                  ? "success"
                  : request.output.decision === "ERROR"
                    ? "error"
                    : "default"
              }
            />
          </Box>
          <Box mt={2}>{renderOutputDetails()}</Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreditMemoSend;
